import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { AXIOS_INSTANCE } from "../api/axios-instance";
import { mutate } from "swr";
import { UserDto } from "../api/model";
import useCookie from "../hooks/useCookie";
import { getGetApiUserKey, useGetApiUser } from "../api/endpoints";
import Loading from "../components/Loading";
import { AxiosRequestConfig } from "axios";

interface AuthContextType {
  user?: UserDto;
  access_token: string | null;
  setAccessToken: (token: string | null) => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>(null!);

const useAuth = () => useContext(AuthContext);

export const useAccessToken = () => {
  return useCookie({
    key: "access_token",
    defaultValue: "",
  });
};

export const useRefreshToken = () => {
  return useCookie({
    key: "refresh_token",
    defaultValue: "",
  });
};

// Auth Provider
function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [accessToken, updateAccessToken] = useAccessToken();
  const [value, setValue] = useState<AuthContextType>({
    access_token: null,
    setAccessToken: (token: string | null) => {
      updateAccessToken(token, 30);
      mutate(getGetApiUserKey());
    },
    isAuthenticated: false,
    isAdmin: false,
    isLoading: true,
  });

  const { data: user } = useGetApiUser({
    swr: {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  });

  useEffect(() => {
    const interceptorId = AXIOS_INSTANCE.interceptors.request.use(
      (config: AxiosRequestConfig<any>) => {
        if (accessToken) {
          config = {
            ...config,
            headers: accessToken
              ? {
                  ...config.headers,
                  Authorization: `Bearer ${accessToken}`,
                }
              : config.headers,
          } as AxiosRequestConfig<any>;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    const responseInterceptorId = AXIOS_INSTANCE.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error("Axios Error: ", error);

        if (error.code === "ERR_NETWORK") {
          navigate("/", { replace: true });
        }

        if (error.response) {
          if ([401, 403, 498].includes(error.response.status)) {
            console.error("Token is invalid or expired.");
            setValue((prev) => ({
              ...prev,
              access_token: null,
              isAuthenticated: false,
              isAdmin: false,
              user: undefined,
            }));
            updateAccessToken(null, 0);

            navigate("/", { replace: true });
          }
        }

        return Promise.reject(error);
      }
    );

    mutate(getGetApiUserKey());

    return () => {
      AXIOS_INSTANCE.interceptors.request.eject(interceptorId);
      AXIOS_INSTANCE.interceptors.response.eject(responseInterceptorId);
    };
  }, [accessToken]);

  useEffect(() => {
    setValue((prevValue) => ({
      ...prevValue,
      user,
      access_token: accessToken,
      isAuthenticated: !!accessToken && !!user,
      isAdmin:
        user?.roles?.some((role) => role.toLowerCase() === "admin") || false,
      isLoading: !user && !!accessToken,
    }));
  }, [user, accessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const auth = useAuth();
  const location = useLocation();

  if (
    !auth.isLoading &&
    (!auth.isAuthenticated || !auth.access_token || !auth.user)
  ) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (auth.isLoading) {
    return <Loading />;
  }

  return children;
}

export { useAuth, RequireAuth, AuthProvider };
